import { useNavigate, useLocation } from "react-router-dom";
import { P } from "@/lib/publicApi";
import ComplaintForm from "@/components/public/ComplaintForm";
import CommendationForm from "@/components/public/CommendationForm";
import CaseTracker from "@/components/public/CaseTracker";
import EvidenceUpload from "@/components/public/EvidenceUpload";
import CfsBoard from "@/components/public/CfsBoard";
import Accountability from "@/components/public/Accountability";
import PoliciesRights from "@/components/public/PoliciesRights";
import { Shield, FileWarning, Award, Search, Upload, Landmark, PhoneCall, TriangleAlert, Siren, BarChart3, ScrollText, FileSearch, Globe } from "lucide-react";
import RecordsRequest from "@/components/public/RecordsRequest";
import OffenderRegistry from "@/components/public/OffenderRegistry";
import ConstableDrop from "@/components/public/ConstableDrop";
import FeedbackLanding from "@/components/public/FeedbackLanding";
import { useLang, LANGS } from "@/lib/publicI18n";

const TILES = [
  { path: "/public/cfs", label: "Live Calls for Service", desc: "Sanitized live dispatch board and community activity map — no officer names, block-level sensitive locations.", icon: Siren, color: "#B98A1E", testid: "tile-cfs" },
  { path: "/public/accountability", label: "Accountability Dashboard", desc: "Aggregate complaint, findings, commendation and response statistics.", icon: BarChart3, color: "#12263A", testid: "tile-accountability" },
  { path: "/public/policies", label: "Policies & Your Rights", desc: "Public Bill of Rights and the agency's public-facing SOP library, organized by category.", icon: ScrollText, color: "#1E7B4F", testid: "tile-policies" },
  { path: "/public/records", label: "Public Records / FOIA Request", desc: "Request eligible public records and track your request status.", icon: FileSearch, color: "#B98A1E", testid: "tile-records" },
  { path: "/public/offender-registry", label: "Sex Offender Registry", desc: "Search official national and state sex offender registries (NSOPW, MD DPSCS, PA Megan's Law and more).", icon: Shield, color: "#B3261E", testid: "tile-offender-registry" },
  { path: "/public/complaint", label: "File a Complaint", desc: "Report employee misconduct or a service concern. Anonymous filing accepted.", icon: FileWarning, color: "#B3261E", testid: "tile-complaint" },
  { path: "/public/commendation", label: "Submit a Commendation", desc: "Recognize an employee for outstanding service, courtesy or life-saving action.", icon: Award, color: "#1E7B4F", testid: "tile-commendation" },
  { path: "/public/case", label: "Check My Case", desc: "Track your case status, read agency responses, and send follow-up messages.", icon: Search, color: "#12263A", testid: "tile-case" },
  { path: "/public/evidence", label: "Upload Evidence", desc: "Securely attach photos, video, audio or documents to an existing case.", icon: Upload, color: "#B98A1E", testid: "tile-evidence" },
  { path: "/public/constable-drop", label: "Constable Records Drop", desc: "Constables: upload civil-process paperwork, returns of service and records to your call for service — badge-verified, no sign-in.", icon: Upload, color: "#12263A", testid: "tile-constable-drop" },
  { path: "/public/contact", label: "Contact Internal Affairs", desc: "Reach the Office of Professional Standards directly.", icon: Landmark, color: "#5B6B7A", testid: "tile-contact" },
];

const Home = ({ go, t }) => (
  <div className="max-w-4xl mx-auto">
    <div className="mb-10">
      <h1 className="text-3xl sm:text-4xl font-bold" style={{ color: P.navy }}>{t("howHelp")}</h1>
      <div className="flex flex-wrap gap-2 mt-3" data-testid="ops-badges">
        {["EST. 2017", "VETERAN OWNED", "MINORITY OWNED", "FEDERALLY COMPLIANT"].map((b) => (
          <span key={b} className="px-2.5 py-1 text-[10px] font-bold tracking-widest rounded-sm border" style={{ borderColor: P.gold, color: "#7A5A0E", background: "#FBF6E9" }}>{b}</span>
        ))}
      </div>
      <p className="text-sm mt-2 max-w-2xl" style={{ color: P.dim }}>{t("homeIntro")}</p>
      <p className="text-xs mt-2 max-w-2xl italic" style={{ color: P.dim }}>{t("langNote")}</p>
    </div>
    <div className="grid sm:grid-cols-2 gap-4">
      {TILES.map((t) => (
        <button key={t.path} data-testid={t.testid} onClick={() => go(t.path)}
          className="text-left p-5 rounded-sm border-2 bg-white transition-transform hover:-translate-y-0.5 hover:shadow-md"
          style={{ borderColor: P.line }}>
          <div className="w-11 h-11 rounded-sm flex items-center justify-center mb-3" style={{ background: `${t.color}14` }}>
            <t.icon size={22} style={{ color: t.color }} />
          </div>
          <div className="font-bold text-base" style={{ color: P.navy }}>{t.label}</div>
          <div className="text-xs mt-1 leading-relaxed" style={{ color: P.dim }}>{t.desc}</div>
        </button>
      ))}
      <div className="p-5 rounded-sm border-2" style={{ borderColor: "#E7D9B9", background: "#FBF6E9" }}>
        <div className="font-bold text-sm" style={{ color: "#7A5A0E" }}>Report Misconduct vs. Report a Crime</div>
        <div className="text-xs mt-1 leading-relaxed" style={{ color: "#7A5A0E" }}>
          This portal reviews <b>employee conduct</b>. To report a crime or request police, fire or EMS service,
          contact your emergency communications center — this portal is <b>not monitored by dispatchers</b>.
        </div>
      </div>
    </div>
  </div>
);

const Contact = () => (
  <div className="max-w-2xl mx-auto space-y-5">
    <div className="flex items-center gap-4">
      <img src="/brand/ia-seal.png" alt="Matcom Internal Affairs seal" data-testid="contact-ia-seal" className="w-20 h-20 rounded-full shrink-0" />
      <div>
        <div className="text-[10px] font-bold uppercase tracking-[0.2em]" style={{ color: P.gold }}>Matcom Internal Affairs</div>
        <h2 className="text-2xl font-bold" style={{ color: P.navy }}>Contact Internal Affairs</h2>
      </div>
    </div>
    <div className="p-5 rounded-sm border space-y-4 bg-white" style={{ borderColor: P.line }}>
      <div>
        <div className="text-xs font-bold uppercase tracking-wider" style={{ color: P.dim }}>Office of Professional Standards</div>
        <div className="text-sm mt-1" style={{ color: P.ink }}>Matcom Office Of Public Safety<br />Internal Affairs Division</div>
      </div>
      <div className="flex items-center gap-2 text-sm" style={{ color: P.ink }}><PhoneCall size={15} style={{ color: P.navy }} /> Business line: contact your agency directory for the current Internal Affairs number.</div>
      <div className="text-sm" style={{ color: P.ink }}>Written correspondence about an existing case should be sent through the <b>Check My Case</b> message center so it becomes part of the immutable case record.</div>
      <div className="text-xs" style={{ color: P.dim }}>Complaints may also be filed in person, by mail, or by telephone. Anonymous complaints are accepted.</div>
    </div>
  </div>
);

export default function PublicPortal() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { lang, setLang, t, rtl } = useLang();
  const go = (p) => { navigate(p); window.scrollTo(0, 0); };
  const view = pathname.replace(/\/+$/, "");
  const NAV = [["/public", t("home")], ["/public/cfs", t("cfs")], ["/public/accountability", t("accountability")], ["/public/policies", t("policies")], ["/public/records", t("records")], ["/public/offender-registry", "Offender Registry"], ["/public/complaint", t("complaint")], ["/public/commendation", t("commendation")], ["/public/case", t("checkCase")], ["/public/evidence", t("evidence")], ["/public/constable-drop", "Constable Drop"], ["/public/contact", t("contact")]];
  return (
      <div data-testid="public-portal" dir={rtl ? "rtl" : "ltr"} className="min-h-screen flex flex-col" style={{ background: P.paper, fontFamily: "Inter, system-ui, sans-serif" }}>
      {/* NOT AN EMERGENCY banner */}
      <div data-testid="public-emergency-banner" className="px-4 py-2 text-center text-[11px] sm:text-xs font-bold text-white flex items-center justify-center gap-2" style={{ background: P.red }}>
        <TriangleAlert size={14} className="shrink-0" />
        {t("banner")}
      </div>
      {/* Header — deliberately separate branding from the internal CAD */}
      <header className="px-4 sm:px-8 py-4" style={{ background: P.navy }}>
        <div className="max-w-5xl mx-auto flex items-center justify-between flex-wrap gap-3">
          <button data-testid="public-home-link" onClick={() => go("/public")} className="flex items-center gap-3 text-left">
            <div className="w-10 h-10 rounded-full flex items-center justify-center border-2" style={{ borderColor: P.gold }}>
              <Shield size={20} style={{ color: P.gold }} />
            </div>
            <div>
              <div className="text-white font-bold text-sm sm:text-base leading-tight">MATCOM PUBLIC SAFETY</div>
              <div className="text-[10px] tracking-widest uppercase" style={{ color: P.gold }}>{t("subtitle")}</div>
            </div>
          </button>
          <div className="flex items-center gap-1" data-testid="public-lang-select-wrap">
            <Globe size={14} style={{ color: P.gold }} />
            <select data-testid="public-lang-select" value={lang} onChange={(e) => setLang(e.target.value)}
              className="bg-transparent text-xs font-semibold outline-none cursor-pointer" style={{ color: "#C6D2DC" }}>
              {LANGS.map(([v, l]) => <option key={v} value={v} style={{ color: "#111" }}>{l}</option>)}
            </select>
          </div>
          <nav className="flex flex-wrap gap-1">
            {NAV.map(([p, label]) => (
              <button key={p} data-testid={`public-nav-${label.replace(/\s+/g, "-").toLowerCase()}`} onClick={() => go(p)}
                className="px-3 py-1.5 text-xs font-semibold rounded-sm transition-colors"
                style={view === p ? { background: P.gold, color: "#fff" } : { color: "#C6D2DC" }}>
                {label}
              </button>
            ))}
          </nav>
        </div>
      </header>
      <main className="flex-1 px-4 sm:px-8 py-10">
        {view === "/public/complaint" || view === "/public/complaints" ? <ComplaintForm onNavigate={go} />
          : view === "/public/commendation" ? <CommendationForm onNavigate={go} />
          : view === "/public/case" ? <CaseTracker />
          : view === "/public/evidence" ? <EvidenceUpload />
          : view === "/public/cfs" ? <CfsBoard />
          : view === "/public/accountability" ? <Accountability />
          : view === "/public/policies" ? <PoliciesRights />
          : view === "/public/records" ? <RecordsRequest />
          : view === "/public/offender-registry" ? <OffenderRegistry />
          : view === "/public/constable-drop" ? <ConstableDrop />
          : view.startsWith("/public/feedback/") ? <FeedbackLanding code={view.split("/")[3]} onNavigate={go} />
          : view === "/public/contact" ? <Contact />
          : <Home go={go} t={t} />}
      </main>
      <footer className="px-4 sm:px-8 py-6" style={{ background: P.navyDeep }}>
        <div className="max-w-5xl mx-auto text-[11px] leading-relaxed" style={{ color: "#8FA1B0" }}>
          <span className="font-bold" style={{ color: "#C6D2DC" }}>Matcom Office Of Public Safety — Office of Professional Standards</span> ·
          Quality Assurance · Personnel Conduct Oversight · Accreditation Management · Policy Development ·
          Anonymous complaints accepted. Complaints alleging criminal conduct are referred for criminal-referral review.
          Information on this portal excludes CJIS, victim, witness, medical, intelligence, tactical and officer-safety data.
          <div className="mt-2">
            <a data-testid="footer-matcomops-link" href="https://matcomops.org/departments" target="_blank" rel="noreferrer" className="underline hover:text-white" style={{ color: P.gold }}>← Matcom Office Public Safety — Specialized Departments</a>
          </div>
        </div>
      </footer>
    </div>
  );
}

